import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
	Box,
	Typography,
	Button,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { generateId } from "common/src/helper/helper";
import { api, store } from "common";
import { useTranslation } from "react-i18next";
import { FirebaseConfig } from "../config/FirebaseConfig";
import { firebase } from "common/src/config/configureFirebase";
import { onValue, off, ref as firebaseRef } from "firebase/database";
import DocumentFieldTable from "components/Table/DocumentFieldTable";
import DocumentUserTable from "components/Table/DocumentUserTable";
import CreateDocumentDialog from "components/Dialog/CreateDocumentDialog";
import AlertDialog from "components/AlertDialog";
import { useNavigate } from "react-router-dom";

// Add a common style for all cells
const cellStyle = {
	verticalAlign: "top",
	paddingTop: 2,
	paddingBottom: 2,
};

// Optimized sorting function - extracted to avoid recreation on every render
const sortDocumentUsers = (users) => {
	return users.sort((a, b) => {
		const getTimestamp = (item) => {
			return item.updated_at || item.created_at || item.reviewed_at || 0;
		};
		return getTimestamp(b) - getTimestamp(a);
	});
};

const DocumentManagementMenu = () => {
	// ✅ OPTIMIZATION: Cache auth token to avoid multiple getIdToken() calls
	const authTokenCache = React.useRef({
		token: null,
		timestamp: null,
		expiresIn: 55 * 60 * 1000, // 55 minutes (token valid for 1 hour, refresh before expiry)
	});

	// Helper function to get auth headers (consistent with existing API patterns)
	const getAuthHeaders = async () => {
		try {
			const user = firebase.auth.currentUser;
			if (!user) {
				throw new Error("User not authenticated");
			}

			// Check if we have a valid cached token
			const now = Date.now();
			const cache = authTokenCache.current;
			
			if (cache.token && cache.timestamp && (now - cache.timestamp) < cache.expiresIn) {
				// Use cached token
				return {
					"Content-Type": "application/json",
					Authorization: `Bearer ${cache.token}`,
				};
			}

			// Get fresh token and cache it
			const idToken = await user.getIdToken();
			authTokenCache.current = {
				...cache,
				token: idToken,
				timestamp: now,
			};

			return {
				"Content-Type": "application/json",
				Authorization: `Bearer ${idToken}`,
			};
		} catch (error) {
			console.error("Error getting auth headers:", error);
			throw new Error("Authentication failed: " + error.message);
		}
	};

	const {
		updateDocumentFields,
		updateDocumentUsers,
		deleteDocumentUser,
		editUser,
		updateUserField,
	} = api;
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const auth = useSelector((state) => state.auth);

	const [open, setOpen] = useState(false);
	const [editDocument, setEditDocument] = useState(null);
	const [alertDialog, setAlertDialog] = useState({
		open: false,
		title: "",
		message: "",
		type: "success",
	});

	const [rejectDialog, setRejectDialog] = useState({
		open: false,
		document: null,
		reason: "",
		isRemove: false,
	});

	// ✅ OPTIMIZATION: State to prevent double-click and show processing state
	const [processingDocuments, setProcessingDocuments] = useState(new Set());
	const documentFields = useSelector((state) => state.documentFieldsData);
	const featureFlags = useSelector((state) => state.featureflagsdata?.data);
	const addons = useSelector((state) => state.addondata?.data);

	const documentUsers = useSelector((state) => state.documentUsersData);
	const [newDocumentCount, setNewDocumentCount] = useState(0);
	const [lastDocumentCount, setLastDocumentCount] = useState(0);

	// Memoized documents list to prevent unnecessary recalculations
	const documents = useMemo(() => {
		if (!documentFields?.data) return [];
		return Object.values(documentFields.data);
	}, [documentFields?.data]);

	// Memoized sorted document users list
	const documentUsersList = useMemo(() => {
		if (!documentUsers?.data) return [];

		const users = Object.values(documentUsers.data).filter((item) => {
			return item && (item.id || item.user_id || item.field_id);
		});

		return sortDocumentUsers(users);
	}, [documentUsers?.data]);

	useEffect(() => {
		// Temporarily disable feature flag check for testing
		// if (featureFlags && addons) {
		//   if (featureFlags.add_on === false || addons.document_management === false) {
		//     navigate('/dashboard', { replace: true });
		//   }
		// }
	}, [featureFlags, addons, navigate]);

	// Real-time Firebase listeners for document users
	useEffect(() => {
		if (!documentUsers?.data) return;

		const documentUsersRef = firebaseRef(
			firebase.database,
			"documentUsers"
		);

		const unsubscribe = onValue(
			documentUsersRef,
			(snapshot) => {
				const data = snapshot.val();
				if (data) {
					const users = Object.values(data).filter((item) => {
						return (
							item && (item.id || item.user_id || item.field_id)
						);
					});

					// Check for new documents
					if (
						users.length > lastDocumentCount &&
						lastDocumentCount > 0
					) {
						const newCount = users.length - lastDocumentCount;
						setNewDocumentCount(newCount);

						// Auto-hide notification after 5 seconds
						setTimeout(() => {
							setNewDocumentCount(0);
						}, 5000);
					}

					setLastDocumentCount(users.length);
				}
			},
			(error) => {
				console.error("Document users listener error:", error);
			}
		);

		return () => {
			off(documentUsersRef);
		};
	}, [lastDocumentCount]);

	// Real-time Firebase listeners for document fields
	useEffect(() => {
		const documentFieldsRef = firebaseRef(
			firebase.database,
			"documentFields"
		);

		const unsubscribe = onValue(
			documentFieldsRef,
			(snapshot) => {
				// Data will be handled by Redux action
			},
			(error) => {
				console.error("Document fields listener error:", error);
			}
		);

		return () => {
			off(documentFieldsRef);
		};
	}, []);

	const handleOpen = useCallback(() => {
		setOpen(true);
	}, []);

	const handleClose = useCallback(() => {
		setOpen(false);
		setEditDocument(null);
	}, []);

	const showAlert = useCallback((title, message, type = "success") => {
		setAlertDialog({
			open: true,
			title,
			message,
			type,
		});
	}, []);

	const closeAlert = useCallback(() => {
		setAlertDialog((prev) => ({
			...prev,
			open: false,
		}));
	}, []);

	const handleEditDocument = useCallback((document) => {
		setEditDocument(document);
		setOpen(true);
	}, []);

	const handleToggleState = useCallback(
		(document) => {
			try {
				dispatch(
					updateDocumentFields(
						{
							...document,
							state: !document.state,
						},
						() => {
							showAlert(t("success"), t("updated"), "success");
						},
						() => {
							showAlert(t("error"), t("update_failed"), "error");
						}
					)
				);
			} catch (error) {
				console.error("Toggle state error:", error);
				showAlert(t("error"), t("update_failed"), "error");
			}
		},
		[dispatch, showAlert, t]
	);

	const handleToggleRequired = useCallback(
		(document) => {
			try {
				dispatch(
					updateDocumentFields(
						{
							...document,
							required: !document.required,
						},
						() => {
							showAlert(t("success"), t("updated"), "success");
						},
						() => {
							showAlert(t("error"), t("update_failed"), "error");
						}
					)
				);
			} catch (error) {
				console.error("Toggle required error:", error);
				showAlert(t("error"), t("update_failed"), "error");
			}
		},
		[dispatch, showAlert, t]
	);

	const handleCreateDocument = useCallback(
		(documentData, isEdit) => {
			try {
				const newDocument = {
					...documentData,
					id: isEdit ? documentData.id : generateId(),
					created_at: isEdit
						? documentData.created_at == null
							? Date.now()
							: documentData.created_at
						: Date.now(),
				};

				setEditDocument(null);
				dispatch(
					updateDocumentFields(
						newDocument,
						() => {
							showAlert(
								t("success"),
								isEdit
									? t("document_updated")
									: t("document_created"),
								"success"
							);
						},
						() => {
							showAlert(t("error"), t("update_failed"), "error");
						}
					)
				);
			} catch (error) {
				console.error("Error creating document:", error);
			}
		},
		[dispatch, showAlert, t]
	);

	const handleApprove = useCallback(
		async (document) => {
			// ✅ OPTIMIZATION: Debounce - prevent double-click
			if (processingDocuments.has(document.id)) {
				console.log(`⚠️ Document ${document.id} is already being processed, ignoring duplicate click`);
				return;
			}

			try {
				// Mark document as processing
				setProcessingDocuments((prev) => new Set(prev).add(document.id));

				// Check if this is a legacy license document
				const isLegacyLicense =
					document.field?.id?.startsWith("legacy_license_");

				if (isLegacyLicense) {
					// Get the legacy field name (licenseImage or licenseImageBack)
					const legacyField =
						document.field.id === "legacy_license_front"
							? "licenseImage"
							: "licenseImageBack";

					// Get the image URL from the approved files
					const imageUrl =
						document.files && document.files.length > 0
							? document.files[0].url
							: null;

					if (imageUrl) {
						// Update user profile with the approved license image
						const updateData = {};
						updateData[legacyField] = imageUrl;

						// Update the user profile using updateUserField to only update specific fields
						dispatch(updateUserField(document.user_id, updateData));
					}
				}

				// Call cloud function to update document status with notification
				const headers = await getAuthHeaders();
				const response = await fetch(
					`https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net/updateDocumentStatus`,
					{
						method: "POST",
						headers,
						body: JSON.stringify({
							documentId: document.id,
							status: "approved",
						}),
					}
				);

				const responseData = await response.json();

				if (response.ok) {
					// ✅ OPTIMIZATION: Remove redundant Firebase write
					// Firebase real-time listener will automatically update the UI
					// No need to dispatch updateDocumentUsers() here
					console.log(`✅ Document ${document.id} approved successfully, waiting for real-time update`);
					
					showAlert(
						t("success"),
						t("document_approved"),
						"success"
					);
				} else {
					console.error(
						"updateDocumentStatus failed with status:",
						response.status
					);
					showAlert(
						t("error"),
						responseData?.error || t("update_failed"),
						"error"
					);
				}
			} catch (error) {
				console.error("Error approving document:", error);
				showAlert(t("error"), t("update_failed"), "error");
			} finally {
				// ✅ Remove from processing set after a delay to prevent rapid re-clicks
				setTimeout(() => {
					setProcessingDocuments((prev) => {
						const newSet = new Set(prev);
						newSet.delete(document.id);
						return newSet;
					});
				}, 1000); // 1 second debounce
			}
		},
		[dispatch, showAlert, t, getAuthHeaders, processingDocuments]
	);

	const handleReject = useCallback((document, isRemove) => {
		setRejectDialog({
			open: true,
			document: document,
			reason: "",
			isRemove: isRemove,
		});
	}, []);

	const handleRejectConfirm = useCallback(async () => {
		const { document, reason, isRemove } = rejectDialog;

		if (!reason.trim()) {
			showAlert(
				t("error"),
				t("rejection_reason_required") ||
					"Rejection reason is required",
				"error"
			);
			return;
		}

		// ✅ OPTIMIZATION: Debounce - prevent double-click
		if (processingDocuments.has(document.id)) {
			console.log(`⚠️ Document ${document.id} is already being processed, ignoring duplicate click`);
			return;
		}

		try {
			// Mark document as processing
			setProcessingDocuments((prev) => new Set(prev).add(document.id));

			// Close dialog first
			setRejectDialog({
				open: false,
				document: null,
				reason: "",
				isRemove: false,
			});

			// ALWAYS send notification first, then handle local deletion if needed
			const headers = await getAuthHeaders();
			const response = await fetch(
				`https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net/updateDocumentStatus`,
				{
					method: "POST",
					headers,
					body: JSON.stringify({
						documentId: document.id,
						status: "rejected",
						reason: reason.trim(),
					}),
				}
			);

			const responseData = await response.json();

			if (response.ok) {
				// ✅ OPTIMIZATION: Remove redundant Firebase write for non-delete case
				// Firebase real-time listener will automatically update the UI
				
				if (isRemove) {
					// Only delete if explicitly needed (no existing files)
					dispatch(
						deleteDocumentUser(
							document.id,
							() => {
								showAlert(
									t("success"),
									t("document_deleted"),
									"success"
								);
							},
							() => {
								showAlert(t("error"), t("delete_failed"), "error");
							}
						)
					);
				} else {
					// No need to dispatch updateDocumentUsers() - listener will handle it
					console.log(`✅ Document ${document.id} rejected successfully, waiting for real-time update`);
					
					showAlert(
						t("success"),
						t("document_rejected"),
						"success"
					);
				}
			} else {
				console.error(
					"updateDocumentStatus failed with status:",
					response.status
				);
				showAlert(
					t("error"),
					responseData?.error || t("update_failed"),
					"error"
				);
			}
		} catch (error) {
			console.error("Error rejecting document:", error);
			showAlert(t("error"), t("update_failed"), "error");
		} finally {
			// ✅ Remove from processing set after a delay to prevent rapid re-clicks
			setTimeout(() => {
				setProcessingDocuments((prev) => {
					const newSet = new Set(prev);
					newSet.delete(document.id);
					return newSet;
				});
			}, 1000); // 1 second debounce
		}
	}, [rejectDialog, showAlert, t, getAuthHeaders, dispatch, processingDocuments]);

	const handleRejectCancel = useCallback(() => {
		setRejectDialog({
			open: false,
			document: null,
			reason: "",
			isRemove: false,
		});
	}, []);

	// Simple test to show if component is loading
	if (!documentFields || !documentUsers) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography variant='h5' component='h1'>
					{t("document_management")}
				</Typography>
				<Typography variant='body1' sx={{ mt: 2 }}>
					Loading document management data...
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			{/* New Document Notification */}
			{newDocumentCount > 0 && (
				<Alert
					severity='info'
					sx={{ mb: 2 }}
					onClose={() => setNewDocumentCount(0)}
				>
					{t("new_documents_uploaded", { count: newDocumentCount })}
				</Alert>
			)}

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}
			>
				<Typography variant='h5' component='h1'>
					{t("document_management")}
				</Typography>
				<Button
					variant='contained'
					color='primary'
					startIcon={<AddIcon />}
					sx={{ borderRadius: 2 }}
					onClick={handleOpen}
				>
					{t("new_document")}
				</Button>
			</Box>

			<CreateDocumentDialog
				open={open}
				onClose={handleClose}
				onSubmit={handleCreateDocument}
				editDocument={editDocument}
			/>

			<DocumentFieldTable
				documents={documents}
				onEditDocument={(document) => handleEditDocument(document)}
				onToggleState={handleToggleState}
				onToggleRequired={handleToggleRequired}
			/>

			<DocumentUserTable
				documentUsers={documentUsersList}
				onApprove={handleApprove}
				onReject={handleReject}
				processingDocuments={processingDocuments}
			/>

			<AlertDialog
				open={alertDialog.open}
				onClose={closeAlert}
				title={alertDialog.title}
				message={alertDialog.message}
				type={alertDialog.type}
			/>

			{/* Rejection Reason Dialog */}
			<Dialog
				open={rejectDialog.open}
				onClose={handleRejectCancel}
				maxWidth='md'
				fullWidth
			>
				<DialogTitle>
					{t("reject_document") || "Reject Document"}
				</DialogTitle>
				<DialogContent>
					<Box sx={{ mt: 2 }}>
						<Typography variant='body1' sx={{ mb: 2 }}>
							{t("document_name") || "Document"}:{" "}
							<strong>
								{rejectDialog.document?.field?.name}
							</strong>
						</Typography>
						<Typography
							variant='body2'
							sx={{ mb: 2, color: "text.secondary" }}
						>
							{t("user") || "User"}:{" "}
							{rejectDialog.document?.user_name} (
							{rejectDialog.document?.user_email})
						</Typography>
						<TextField
							fullWidth
							multiline
							rows={4}
							label={t("rejection_reason") || "Rejection Reason"}
							value={rejectDialog.reason}
							onChange={(e) =>
								setRejectDialog((prev) => ({
									...prev,
									reason: e.target.value,
								}))
							}
							placeholder={
								t("rejection_reason_placeholder") ||
								"Please provide a clear reason for rejecting this document..."
							}
							required
							sx={{ mb: 2 }}
						/>
						<Typography variant='caption' color='text.secondary'>
							{t("rejection_reason_help") ||
								"This reason will be sent to the user in the notification."}
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleRejectCancel} color='inherit'>
						{t("cancel") || "Cancel"}
					</Button>
					<Button
						onClick={handleRejectConfirm}
						color='error'
						variant='contained'
						disabled={!rejectDialog.reason.trim()}
					>
						{t("reject") || "Reject"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default DocumentManagementMenu;
